import { useState, useRef, useMemo, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { collection,  query, where } from 'firebase/firestore'

import PageHeader from '@common/src/pageheader/pageheader'
import LocalSearchTableList from '@common/src/common/Table/LocalSearchTableList'
import { useDebounce } from '@common/src/common/hook/useDebounce'
import SelectBox from '@common/src/common/selectbox/selectbox'
import { MAP_STATUS, MAP_USER_TYPES } from '../constants/map'
import { COLLECTIONS, SUBADMIN_TYPES } from '../constants/constants'

import { homeDb as db } from '../../../src/firebase/firebaseapi' // global data: always the home database
import { availableRegions } from '../config/regions'
import FloatingCanvas from '../common/offcanvas/floatingCanvas'
import useFloatingCanvas from '../hooks/useFloatingCanvas'
import AddUser from './addusers'
import { resendInviteMail } from '../service/cloud/users'
import { useToast } from '@common/src/common/Toast/ToastProvider';

const SEARCH_FIELDS = ['firstName', 'lastName', 'email']

const getColums = (permissions, onEditClick,onResendClick) => {
  return [
    {
      title: 'First Name',
      dataIndex: 'firstName',
      key: 'firtsName',
    },
    {
      title: 'Last Name',
      dataIndex: 'lastName',
      key: 'lastName',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (_, row) => (
        <>
          {row.roles?.map((role) => {
            return (
              <span className="badge bg-light text-default" key={role.name}>
                {role.name}
              </span>
            )
          })}
        </>
      ),
    },
    {
      title: 'User Type',
      dataIndex: 'userType',
      key: 'userType',
      render: (_, row) => <>{MAP_USER_TYPES[row.userType]}</>,
    },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (_, row) => <>{MAP_STATUS[row.status]}</> },
    {
      title: 'Action',
      key: 'action',
      className: 'text-start',
      render: (_, record) => (
        <div className="flex flex-row items-center !gap-2 text-[0.9375rem]">
          <Link
            aria-label="anchor"
            to="#"
            onClick={() => onEditClick(record)}
            className="ti-btn ti-btn-wave !gap-0 !m-0 !h-[1.75rem] !w-[1.75rem] text-[0.8rem] bg-info/10 text-info hover:bg-info hover:text-white hover:border-info"
          >
            <i className="ri-pencil-line"></i>
          </Link>
          {record.status === 'PENDING' && (
            <Link
              aria-label="anchor"
              to="#"
              onClick={() => onResendClick(record?.id)}
              className="ti-btn ti-btn-wave !gap-0 !m-0 !h-[1.75rem] !w-[1.75rem] text-[0.8rem] bg-info/10 text-info hover:bg-info hover:text-white hover:border-info"
            >
              <i className="ri-mail-line"></i>
            </Link>
          )}
        </div>
        
      ),
    },
  ]
}

let ref = collection(db, COLLECTIONS.USERS)
ref = query(ref, where('userType', 'in', SUBADMIN_TYPES[import.meta.env.VITE_PORTAL]), where('isDeleted', '==', false))
const pagination = {
  pageSize: 20,
}

// Pulse admins work in home, so the region to list is picked on this page (no header switch). Only regions
// configured in this deployment are offered; with home alone, the dropdown is hidden.
const REGION_OPTIONS = availableRegions.map((region) => ({ label: region.label, value: region.key }))

const User = () => {
  const tableRef = useRef(null)
  const toast = useToast();
  const [data, setData] = useState({})
  const [isEdit, setIsEdit] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearchTerm = useDebounce(searchTerm, 300)
  const [region, setRegion] = useState('home')
  // Users are global (home database). No `region` on the doc means home. Filtered client-side because
  // Firestore can't query a missing field.
  const isInRegion = useCallback((user) => (user?.region || 'home') === region, [region])
  const { isVisible, activeComponent, offCanvasTitle, openOffCanvas, closeOffCanvas } = useFloatingCanvas()

  const onEditClick = (data) => {
    setData(data)
    setIsEdit(true)
    openOffCanvas('editUser', 'Edit User')
  }
  const onResendClick = async (userId) => {
    try {
    
      const data = { uid: String(userId) };
      await resendInviteMail(data);
      toast.success('Mail sent successfully!');
    } catch (error) {
      console.error('Error sending mail:', error);
      toast.error('Failed to send mail. Please try again.');
    }
  };
  const columns = useMemo(() => getColums('permissions', onEditClick,onResendClick), [])
  const onComplete = () => {
    if (tableRef.current) {
      tableRef.current.refreshCurrentPage()
      closeOffCanvas()
    }
  }
  const onAddClick = () => {
    setData(null)
    setIsEdit(false)
    openOffCanvas('addUser', 'Add User')
  }
  return (
    <>
      <PageHeader currentpage="User" activepage="User Management" mainpage="users" />
      <LocalSearchTableList
        ref={tableRef}
        name="User List"
        columns={columns}
        dbRef={ref}
        rowFilter={isInRegion}
        toolbar={
          REGION_OPTIONS.length > 1 && (
            <div className="w-48">
              <SelectBox
                id="users-region"
                name="usersRegion"
                options={REGION_OPTIONS}
                value={REGION_OPTIONS.find((option) => option.value === region)}
                onChange={(e) => setRegion(e.value)}
                isSearchable={false}
              />
            </div>
          )
        }
        pagination={pagination}
        searchTerm={debouncedSearchTerm}
        searchFields={SEARCH_FIELDS}
        searchInputValue={searchTerm}
        onSearchInputChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search by name or email"
        tools={{
          btnName: 'Add User',
          btnIcon: 'ri-add-line',
          btnClick: onAddClick,
        }}
      />
      <FloatingCanvas
        id={'hs-test-123'}
        onClose={() => closeOffCanvas(false)}
        isVisible={isVisible}
        close={closeOffCanvas}
        title={offCanvasTitle}
        okText="Submit"
        closeText="Close"
      >
        {['addUser', 'editUser'].includes(activeComponent) && (
          <AddUser data={data} isEdit={isEdit} defaultRegion={region} onComplete={onComplete} />
        )}
      </FloatingCanvas>
    </>
  )
}
export default User
